import { z } from 'zod';
import { IANATimezoneSchema } from '../common/timezone.schema.js';
import { CreditHoldStatusSchema, DunningProfileSchema, FacilityContractStateSchema, FacilityStatusSchema, FacilityTypeSchema, InvoiceCadenceSchema, } from './inquiry.schema.js';
import { AdminFacilityUserRowSchema } from './user-admin.schema.js';
import { HstTreatmentSchema } from '../rate/enums.schema.js';
// CreditHoldStatusSchema + FacilityContractStateSchema live in inquiry.schema.ts (facility enums, Convention 06); re-exported via the barrel.
// --------- list ---------
/** GET /api/v1/admin/facilities — filters + offset pagination.
 *
 *  Sort: status ASC, createdAt DESC. PROSPECT rows surface first (newest
 *  leads at the top of the queue), then ACTIVE, then SUSPENDED, then
 *  TERMINATED. */
export const AdminFacilityListQuerySchema = z.object({
    status: FacilityStatusSchema.optional(),
    type: FacilityTypeSchema.optional(),
    /** Phase 14b-2-4 — contract-state filter. See FacilityContractStateSchema. */
    contractState: FacilityContractStateSchema.optional(),
    /** Phase 14b-2-4 — credit-hold-state filter. Operator can isolate
     *  WARNED or ACTIVE facilities for follow-up. */
    creditHoldStatus: CreditHoldStatusSchema.optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
});
export const AdminFacilityListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** Phase 14b-2-5 — 6-hex platform-generated code per 10a; surfaces
     *  in admin list so operators can correlate with INV-{year}W{week}-
     *  {facility.code}-{seq} invoice numbers at a glance. Nullable for
     *  back-compat with pre-Phase-10a rows (migration backfilled — null
     *  should not occur at runtime, but typing matches Prisma). */
    code: z.string().nullable(),
    type: FacilityTypeSchema,
    status: FacilityStatusSchema,
    city: z.string().nullable(),
    primaryContactName: z.string().nullable(),
    primaryContactEmail: z.string().nullable(),
    /** True iff a facility_contract row exists with effective_to IS NULL.
     *  Kept for backward compat; `contractState` below is the richer view. */
    hasActiveContract: z.boolean(),
    /** Phase 14b-2-5 — derived; mirrors the filter enum. */
    contractState: FacilityContractStateSchema,
    /** Phase 14b-2-5 — current credit-hold state from 10e dunning. */
    creditHoldStatus: CreditHoldStatusSchema,
    userCount: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    activatedAt: z.string().datetime().nullable(),
});
export const AdminFacilityListResponseSchema = z.object({
    items: z.array(AdminFacilityListItemSchema),
    totalCount: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
// --------- detail ---------
export const AdminFacilityContractRowSchema = z.object({
    id: z.string().uuid(),
    effectiveFrom: z.string().date(),
    effectiveTo: z.string().date().nullable(),
    poReference: z.string().nullable(),
    invoiceCadence: InvoiceCadenceSchema,
    netTermsDays: z.number().int(),
    dunningProfile: DunningProfileSchema,
    interestPerMonthPct: z.number(),
    contractDocKey: z.string().nullable(),
    createdAt: z.string().datetime(),
});
// `AdminFacilityUserRowSchema` extracted to `user-admin.schema.ts` per
// Convention 06 (250-LOC cap on per-resource schema files).
export const AdminFacilityDetailResponseSchema = z.object({
    id: z.string().uuid(),
    /** Phase 14b-2-5 — 6-hex platform-generated code per 10a (also in list).
     *  Nullable for back-compat with pre-Phase-10a rows. */
    code: z.string().nullable(),
    name: z.string(),
    dbaName: z.string().nullable(),
    type: FacilityTypeSchema,
    status: FacilityStatusSchema,
    /** Phase 14b-2-5 — current credit-hold state from 10e dunning. */
    creditHoldStatus: CreditHoldStatusSchema,
    /** Per-facility HST treatment (2026-09-21) — uniform across roles; drives facility-AR HST at booking. */
    hstTreatment: HstTreatmentSchema,
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    city: z.string().nullable(),
    province: z.string(),
    postalCode: z.string().nullable(),
    /** Phase 14e-a-9 — geocoded lat/lng from address (set by
     *  mapbox.geocode hook at create/patch/inquiry + bootstrap fill).
     *  Snapshotted into ShiftRequest.locationLat/Lng at request create.
     *  NULL when address incomplete OR Mapbox soft-degraded. Read-only. */
    addressGeocodeLat: z.string().nullable(),
    addressGeocodeLng: z.string().nullable(),
    addressGeocodedAt: z.string().datetime().nullable(),
    /** IANA timezone — drives the rate-engine time-bucket + booking scheduling in local time. */
    timezone: IANATimezoneSchema,
    primaryContactName: z.string().nullable(),
    primaryContactRole: z.string().nullable(),
    primaryContactEmail: z.string().nullable(),
    primaryContactPhone: z.string().nullable(),
    billingContactEmail: z.string().nullable(),
    primaryContactEmailEditable: z.boolean(), // true while the primary contact is a never-logged-in invitee (email still correctable); see facility-admin.service.ts
    /** Only meaningful while status = PROSPECT, but kept for paper trail. */
    inquiryEstimatedBeds: z.number().int().nullable(),
    inquiryEstimatedShiftsPerWeek: z.number().int().nullable(),
    inquiryBestTimeToReach: z.string().nullable(),
    inquiryMessage: z.string().nullable(),
    activatedAt: z.string().datetime().nullable(),
    suspendedAt: z.string().datetime().nullable(),
    suspensionReason: z.string().nullable(),
    terminatedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    /** Full version history, newest first. */
    contracts: z.array(AdminFacilityContractRowSchema),
    users: z.array(AdminFacilityUserRowSchema),
});
// --------- create (operator-initiated; NOT via the public inquiry endpoint) ---------
/** POST /api/v1/admin/facilities — operator creates a facility directly
 *  (e.g. they spoke to the prospect on the phone and want to skip the
 *  inquiry form). Defaults status to PROSPECT; lifecycle endpoints move
 *  it through the rest of the states. */
export const AdminFacilityCreateRequestSchema = z.object({
    name: z.string().min(1).max(200).trim(),
    dbaName: z.string().max(200).trim().nullable().optional(),
    type: FacilityTypeSchema,
    /** Per-facility HST treatment (2026-09-21). Default TAXABLE; operator sets
     *  EXEMPT/ZERO_RATED for exempt facilities. Uniform across all roles. */
    hstTreatment: HstTreatmentSchema.default('TAXABLE'),
    addressLine1: z.string().max(200).trim().nullable().optional(),
    addressLine2: z.string().max(200).trim().nullable().optional(),
    city: z.string().max(80).trim().nullable().optional(),
    postalCode: z.string().max(10).trim().nullable().optional(),
    /** IANA timezone. Defaults to America/Toronto (Eastern — covers
     *  ~95% of Ontario). Operator selects America/Atikokan for
     *  Kenora-area facilities. Validated via IANATimezoneSchema. */
    timezone: IANATimezoneSchema.default('America/Toronto'),
    primaryContactName: z.string().min(1).max(120).trim(),
    primaryContactRole: z.string().max(80).trim().nullable().optional(),
    primaryContactEmail: z.string().email().max(254).toLowerCase(),
    primaryContactPhone: z.string().max(30).trim().nullable().optional(),
    billingContactEmail: z.string().email().max(254).toLowerCase().nullable().optional(),
});
// --------- patch (non-financial fields only) ---------
/** PATCH /api/v1/admin/facilities/:id — operator-permitted edits to
 *  non-financial fields. Contract terms (cadence, dunning, interest)
 *  go through the /contract endpoint so versioning is preserved.
 *
 *  Free-text `poReference` is an exception per Q9.A — it's a label, not
 *  a billing term, and operators routinely correct it after a facility
 *  emails them an updated PO. Edited via this endpoint, NOT via amend. */
export const AdminFacilityPatchRequestSchema = z.object({
    name: z.string().min(1).max(200).trim().optional(),
    dbaName: z.string().max(200).trim().nullable().optional(),
    addressLine1: z.string().max(200).trim().nullable().optional(),
    addressLine2: z.string().max(200).trim().nullable().optional(),
    city: z.string().max(80).trim().nullable().optional(),
    postalCode: z.string().max(10).trim().nullable().optional(),
    timezone: IANATimezoneSchema.optional(),
    primaryContactName: z.string().max(120).trim().nullable().optional(),
    primaryContactRole: z.string().max(80).trim().nullable().optional(),
    primaryContactEmail: z.string().email().max(254).toLowerCase().nullable().optional(),
    primaryContactPhone: z.string().max(30).trim().nullable().optional(),
    billingContactEmail: z.string().email().max(254).toLowerCase().nullable().optional(),
    /** Per-facility HST treatment (2026-09-21). Forward-only — past shifts keep
     *  their snapshotted hstTreatmentAtBooking; only future bookings change. */
    hstTreatment: HstTreatmentSchema.optional(),
    /** Updates the *current* (effective_to IS NULL) contract row's
     *  po_reference in place. No-op if there's no current contract. */
    currentContractPoReference: z.string().max(80).trim().nullable().optional(),
});
// --------- amend contract (creates new row, closes prior) ---------
/** POST /api/v1/admin/facilities/:id/contract — append a new contract
 *  row, closing the current row's effective_to to (effective_from - 1
 *  day). Versioned per Principle #4. */
export const AdminFacilityContractAmendRequestSchema = z.object({
    effectiveFrom: z.string().date(),
    poReference: z.string().max(80).trim().nullable().optional(),
    invoiceCadence: InvoiceCadenceSchema.default('BIWEEKLY'),
    netTermsDays: z.coerce.number().int().positive().max(120).default(30),
    dunningProfile: DunningProfileSchema.default('STANDARD'),
    /** 0–10% per month inclusive; default 1.5 per Q9.A. */
    // Audit H3 (2026-07-28): cap at 5 to match the interest_charge CHECK (0..5);
    // >5% failed every monthly accrual INSERT into a swallowed failures++.
    interestPerMonthPct: z.coerce.number().nonnegative().max(5, 'Interest rate cannot exceed 5%/month').default(1.5),
    // No contractDocKey here (audit fix 2026-08-11): the signed PDF is attached
    // via the validated PUT /contract/doc path, not accepted as a raw key.
});
// --------- lifecycle ---------
export const AdminFacilityActivateRequestSchema = z.object({}).strict();
export const AdminFacilitySuspendRequestSchema = z.object({
    reason: z.string().min(1).max(500).trim(),
});
export const AdminFacilityUnsuspendRequestSchema = z.object({}).strict();
export const AdminFacilityTerminateRequestSchema = z.object({
    reason: z.string().min(1).max(500).trim(),
});
export const AdminFacilityStatusResponseSchema = z.object({
    id: z.string().uuid(),
    status: FacilityStatusSchema,
    activatedAt: z.string().datetime().nullable(),
    suspendedAt: z.string().datetime().nullable(),
    suspensionReason: z.string().nullable(),
    terminatedAt: z.string().datetime().nullable(),
});
// User-invite + remove resource extracted to `user-admin.schema.ts`.
//# sourceMappingURL=admin.schema.js.map